import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * API Service for Theme Settings Backend
 * Handles all HTTP requests to the Python FastAPI backend
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeApiService {
  private apiUrl = `${environment.apiBaseUrlPython}/theme-settings`;

  constructor(private http: HttpClient) {}

  /**
   * Get theme settings for a specific company
   */
  getThemeForCompany(companyId: number): Observable<ThemeApiResponse> {
    const params = new HttpParams().set('company_id', companyId.toString());
    
    return this.http.get<ThemeApiResponse>(this.apiUrl, { params }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get all theme settings (admin function)
   */
  getAllThemes(activeOnly: boolean = true): Observable<ThemeListApiResponse> {
    const params = new HttpParams().set('active_only', activeOnly.toString());
    
    return this.http.get<ThemeListApiResponse>(`${this.apiUrl}/all`, { params }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Create new theme settings for a company
   */
  createTheme(themeData: ThemeApiRequest): Observable<ThemeApiResponse> {
    return this.http.post<ThemeApiResponse>(this.apiUrl, themeData).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Update existing theme settings for a company
   */
  updateTheme(companyId: number, themeData: ThemeApiRequest): Observable<ThemeApiResponse> {
    return this.http.put<ThemeApiResponse>(`${this.apiUrl}/${companyId}`, themeData).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Delete theme settings for a company
   */
  deleteTheme(companyId: number, hardDelete: boolean = false): Observable<any> {
    const params = new HttpParams().set('hard_delete', hardDelete.toString());
    
    return this.http.delete(`${this.apiUrl}/${companyId}`, { params }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Error handler
   */
  private handleError(error: any): Observable<never> {
    console.error('Theme API Error:', error);
    
    let errorMessage = 'An error occurred while processing your request.';
    
    if (error.error && error.error.detail) {
      errorMessage = error.error.detail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return throwError(() => new Error(errorMessage));
  }
}

/**
 * API Response Interfaces
 */
export interface ThemeApiResponse {
  success: boolean;
  message: string;
  data: ThemeApiData | null;
}

export interface ThemeListApiResponse {
  success: boolean;
  message: string;
  data: ThemeApiData[];
  total_count: number;
}

export interface ThemeApiData {
  theme_settings_id?: number;
  company_id: number;
  theme_id?: string;
  colors: {
    primary: string;
    primaryLight?: string;
    primaryDark?: string;
    secondary?: string;
    secondaryLight?: string;
    secondaryDark?: string;
    accent?: string;
    background?: string;
    text?: string;
  };
  gradients?: {
    primary?: string;
    hover?: string;
    light?: string;
  };
  shadows?: {
    primary?: string;
    large?: string;
    small?: string;
  };
  branding?: {
    logoUrl?: string;
    logoSmallUrl?: string;
    loginBannerUrl?: string;
    emailLogoUrl?: string;
  };
  typography?: {
    fontFamily?: string;
    fontFamilySecondary?: string;
  };
  layout?: {
    borderRadius?: string;
    layoutStyle?: string;
  };
  themeMode?: {
    enableDarkMode?: boolean;
    defaultMode?: string;
  };
  customizations?: {
    customCss?: string;
    customJs?: string;
  };
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ThemeApiRequest {
  company_id: number;
  theme_id?: string;
  colors: {
    primary: string;
    primaryLight?: string;
    primaryDark?: string;
    secondary?: string;
    secondaryLight?: string;
    secondaryDark?: string;
    accent?: string;
    background?: string;
    text?: string;
  };
  gradients?: {
    primary?: string;
    hover?: string;
    light?: string;
  };
  shadows?: {
    primary?: string;
    large?: string;
    small?: string;
  };
  branding?: {
    logoUrl?: string;
    logoSmallUrl?: string;
    loginBannerUrl?: string;
    emailLogoUrl?: string;
  };
  typography?: {
    fontFamily?: string;
    fontFamilySecondary?: string;
  };
  layout?: {
    borderRadius?: string;
    layoutStyle?: string;
  };
  themeMode?: {
    enableDarkMode?: boolean;
    defaultMode?: string;
  };
  customizations?: {
    customCss?: string;
    customJs?: string;
  };
  is_active?: boolean;
}
